/**
 * Payment Integration Models
 * Based on old QMC QPilotPaymentIntegration and PaymentIntegrationsController.cs
 */

/**
 * Payment Integration entity (API response shape)
 * PascalCase to match API response
 */
export interface PaymentIntegration {
  Id: number;
  SiteId: number;
  PaymentMethodType: string;
  ApiAccount: string | null;
  ApiKey1: string | null;
  ApiKey2: string | null;
  ApiKey3: string | null;
  TestMode: boolean;
  AuthorizeOnly: boolean;
  CreatedUtc: string;
  UpdatedUtc: string;
  GatewayPaymentType: string | null;
}

/**
 * DTO for creating a new payment integration
 * Matches PostPutPaymentIntegrationDto on API
 */
export interface PaymentIntegrationCreateDto {
  SiteId: number;
  PaymentMethodType: string;
  ApiAccount?: string | null;
  ApiKey1?: string | null;
  ApiKey2?: string | null;
  ApiKey3?: string | null;
  TestMode: boolean;
  AuthorizeOnly?: boolean;
  GatewayPaymentType?: string | null;
}

/**
 * DTO for updating an existing payment integration
 * Same structure as create
 */
export interface PaymentIntegrationUpdateDto {
  PaymentMethodType: string;
  ApiAccount?: string | null;
  ApiKey1?: string | null;
  ApiKey2?: string | null;
  ApiKey3?: string | null;
  TestMode: boolean;
  AuthorizeOnly?: boolean;
  GatewayPaymentType?: string | null;
}

/**
 * Filter parameters for list endpoint
 * Note: API only supports pagination and sorting, not filtering by field values
 */
export interface PaymentIntegrationFilterParams {
  siteId: number;
  page: number;
  pageSize: number;
  orderBy?: string;
  order?: "asc" | "desc";
  // Client-side filters (not sent to API, applied after fetch)
  clientFilterId?: number | null;
  clientFilterName?: string | null;
  clientFilterProvider?: string | null;
  clientFilterTestMode?: boolean | null;
}

/**
 * Default filter values
 */
export const DEFAULT_PAYMENT_INTEGRATION_FILTER: Partial<PaymentIntegrationFilterParams> =
  {
    page: 1,
    pageSize: 25,
    orderBy: "UpdatedUtc",
    order: "desc",
  };

/**
 * Paginated response from API
 */
export interface PaymentIntegrationListResponse {
  Items: PaymentIntegration[];
  TotalCount: number;
  Page: number;
  PageSize: number;
}

/**
 * Payment method type display names
 * Migrated from old QMC PaymentIntegrationsHelper
 */
export const PAYMENT_METHOD_DISPLAY_NAMES: Record<string, string> = {
  Stripe: "Stripe",
  AuthorizeNet: "Authorize.Net",
  PayPal: "PayPal",
  Braintree: "Braintree",
  CyberSourceV2: "CyberSource v2",
  Nmi: "NMI",
  PayaV1: "Paya v1",
  TrustCommerce: "TrustCommerce",
  Square: "Square",
  Sage: "SagePay/Opayo",
  Checkout: "Checkout.com",
  PaypalV2: "PayPal v2",
  PayPalV3: "PayPal v3",
  Airwallex: "Airwallex",
  Shopify: "Shopify",
  Other: "Other",
};

/**
 * Payment integration field configurations
 * Migrated from old QMC PaymentIntegrationsHelper
 */
export interface PaymentIntegrationConfig {
  ApiAccount?: string | null;
  ApiKey1?: string | null;
  ApiKey2?: string | null;
  ApiKey3?: string | null;
  GatewayPaymentType?: { code: string; name: string }[];
  TestMode: boolean;
  AuthorizeOnly: boolean;
}

export const PAYMENT_INTEGRATION_CONFIGS: Record<
  string,
  PaymentIntegrationConfig
> = {
  Stripe: {
    ApiAccount: null,
    ApiKey1: "Secret Key",
    ApiKey2: "Publishable Key",
    GatewayPaymentType: [
      { code: "0", name: "Automated Clearing Settlement System" },
      { code: "1", name: "Afterpay / Clearpay" },
      { code: "2", name: "Alipay" },
      { code: "3", name: "BECS Direct Debit" },
      { code: "4", name: "Bacs Direct Debit" },
      { code: "5", name: "Bancontact" },
      { code: "6", name: "Boleto" },
      { code: "7", name: "Card" },
      { code: "8", name: "Stripe Terminal" },
      { code: "9", name: "Cash Balance" },
      { code: "10", name: "Eps" },
      { code: "11", name: "Fpx" },
      { code: "12", name: "Giropay" },
      { code: "13", name: "Grabpay" },
      { code: "14", name: "Ideal" },
      { code: "15", name: "Interac debit cards" },
      { code: "16", name: "Klarna" },
      { code: "17", name: "Konbini" },
      { code: "18", name: "Oxxo" },
      { code: "19", name: "Przelewy24" },
      { code: "20", name: "Paynow" },
      { code: "21", name: "SEPA Direct Debit" },
      { code: "22", name: "Sofort" },
      { code: "23", name: "ACH Direct Debit" },
      { code: "24", name: "WeChat Pay" },
      { code: "32", name: "Link" },
    ],
    TestMode: true,
    AuthorizeOnly: false,
  },
  AuthorizeNet: {
    ApiAccount: "API Login ID",
    ApiKey1: "Transaction Key",
    TestMode: true,
    AuthorizeOnly: false,
  },
  PayPal: {
    ApiAccount: "User",
    ApiKey1: "Password",
    ApiKey2: "Signature",
    GatewayPaymentType: [
      { code: "25", name: "paypal_FUNDING_PAYPAL" },
      { code: "26", name: "paypal_FUNDING_CARD" },
      { code: "27", name: "paypal_FUNDING_PAYLATER" },
      { code: "28", name: "paypal_FUNDING_CREDIT" },
      { code: "29", name: "paypal_FUNDING_VENMO" },
    ],
    TestMode: true,
    AuthorizeOnly: false,
  },
  Braintree: {
    ApiAccount: "Merchant ID",
    ApiKey1: "Public Key",
    ApiKey2: "Private Key",
    ApiKey3: "Merchant Account ID",
    TestMode: true,
    AuthorizeOnly: false,
  },
  CyberSourceV2: {
    ApiAccount: "Merchant ID",
    ApiKey1: "Key Detail",
    ApiKey2: "Shared Secret Key",
    TestMode: true,
    AuthorizeOnly: true,
  },
  Nmi: {
    ApiAccount: "Username",
    ApiKey1: "Password",
    TestMode: false,
    AuthorizeOnly: false,
  },
  PayaV1: {
    ApiAccount: "Merchant ID",
    ApiKey1: "Merchant key",
    TestMode: true,
    AuthorizeOnly: true,
  },
  PaypalV2: {
    ApiAccount: "PayPal Merchant ID",
    ApiKey1: "Client ID",
    ApiKey2: "Secret",
    TestMode: true,
    AuthorizeOnly: true,
  },
  PayPalV3: {
    ApiAccount: "PayPal Merchant ID",
    ApiKey1: "Client ID",
    ApiKey2: "Secret",
    TestMode: true,
    AuthorizeOnly: true,
  },
  TrustCommerce: {
    ApiAccount: "Customer ID",
    ApiKey1: "Password",
    TestMode: false,
    AuthorizeOnly: true,
  },
  Square: {
    ApiKey1: "Access Token",
    ApiKey2: "Location ID",
    TestMode: true,
    AuthorizeOnly: false,
  },
  Sage: {
    ApiAccount: null,
    ApiKey1: "Integration Key",
    ApiKey2: "Integration Password",
    TestMode: true,
    AuthorizeOnly: false,
  },
  Checkout: {
    ApiAccount: null,
    ApiKey1: "Public Key",
    ApiKey2: "Secret Key",
    TestMode: true,
    AuthorizeOnly: true,
  },
  Shopify: {
    ApiAccount: "Shopify Merchant ID",
    ApiKey1: "Public Key",
    ApiKey2: "Integration Password",
    TestMode: true,
    AuthorizeOnly: false,
  },
  Airwallex: {
    ApiAccount: "Client ID",
    ApiKey1: "API Key",
    TestMode: true,
    AuthorizeOnly: false,
  },
  Other: {
    TestMode: false,
    AuthorizeOnly: false,
  },
};

/**
 * Get available payment method types
 */
export function getPaymentMethodTypes(): string[] {
  return Object.keys(PAYMENT_INTEGRATION_CONFIGS);
}

/**
 * Get display name for a payment method type
 */
export function getPaymentMethodDisplayName(paymentMethodType: string): string {
  return PAYMENT_METHOD_DISPLAY_NAMES[paymentMethodType] || paymentMethodType;
}

/**
 * Get field display name for a payment method type
 */
export function getFieldDisplayName(
  paymentMethodType: string,
  fieldName: "ApiAccount" | "ApiKey1" | "ApiKey2" | "ApiKey3",
): string | null {
  const config = PAYMENT_INTEGRATION_CONFIGS[paymentMethodType];
  if (!config) return null;
  return config[fieldName] ?? null;
}

/**
 * Check if a payment method type supports test mode
 */
export function canEnableTestMode(paymentMethodType: string): boolean {
  const config = PAYMENT_INTEGRATION_CONFIGS[paymentMethodType];
  return config?.TestMode ?? false;
}

/**
 * Check if a payment method type supports authorize only
 */
export function canAuthorizeOnly(paymentMethodType: string): boolean {
  const config = PAYMENT_INTEGRATION_CONFIGS[paymentMethodType];
  return config?.AuthorizeOnly ?? false;
}

/**
 * Get gateway payment types for a payment method
 */
export function getGatewayPaymentTypes(
  paymentMethodType: string,
): { code: string; name: string }[] | null {
  const config = PAYMENT_INTEGRATION_CONFIGS[paymentMethodType];
  return config?.GatewayPaymentType ?? null;
}
